import { useState } from 'react';
import { Box, Typography } from '@mui/material';
import AddRoundedIcon from '@mui/icons-material/AddRounded';
import CloseRoundedIcon from '@mui/icons-material/CloseRounded';

// Galeri ala byd.com: deretan kartu foto. Kartu yang aktif melebar dan
// memunculkan panel teks di sampingnya (di layar kecil: di bawah foto).
// Tombol + membuka kartu, tombol x menutupnya.
//
// items: [{ src, alt?, title, desc }]
// defaultIndex: kartu yang terbuka saat pertama tampil (null = semua tertutup)
// height: tinggi deretan di desktop (px)

const EASE = 'cubic-bezier(0.4, 0, 0.2, 1)';

function ExpandingCards({ items, defaultIndex = 0, height = 520, modelName = '' }) {
  const [active, setActive] = useState(defaultIndex);

  return (
    <Box
      role='list'
      sx={{
        display: 'flex',
        flexDirection: { xs: 'column', md: 'row' },
        gap: 2,
        height: { xs: 'auto', md: height },
      }}
    >
      {items.map((item, index) => {
        const isActive = index === active;
        const open = () => setActive(index);
        const close = () => setActive(null);

        return (
          <Box
            key={item.title}
            role='listitem'
            onClick={isActive ? undefined : open}
            sx={{
              display: 'flex',
              flexDirection: { xs: 'column', md: 'row' },
              flex: { xs: '0 0 auto', md: isActive ? '1.9 1 0%' : '1 1 0%' },
              minWidth: 0,
              borderRadius: '18px',
              overflow: 'hidden',
              backgroundColor: '#f3f4f6',
              cursor: isActive ? 'default' : 'pointer',
              transition: `flex 0.6s ${EASE}`,
            }}
          >
            {/* foto + judul + tombol */}
            <Box
              sx={{
                position: 'relative',
                flex: '0 0 auto',
                width: { xs: '100%', md: isActive ? '52.6%' : '100%' },
                height: { xs: isActive ? 340 : 240, md: '100%' },
                borderRadius: '18px',
                overflow: 'hidden',
                backgroundColor: '#d8d2cc',
                transition: `width 0.6s ${EASE}, height 0.5s ${EASE}`,
                '&:hover img': { transform: isActive ? 'none' : 'scale(1.04)' },
              }}
            >
              <Box
                component='img'
                src={item.src}
                alt={item.alt || `${modelName}: ${item.title}`}
                loading='lazy'
                sx={{
                  position: 'absolute',
                  top: 0,
                  right: 0,
                  bottom: 0,
                  left: 0,
                  width: '100%',
                  height: '100%',
                  objectFit: 'cover',
                  objectPosition: item.position || 'center',
                  display: 'block',
                  transition: 'transform 0.6s ease',
                }}
              />
              <Box
                sx={{
                  position: 'absolute',
                  top: 0,
                  left: 0,
                  right: 0,
                  height: '45%',
                  background: 'linear-gradient(180deg, rgba(0,0,0,0.42) 0%, rgba(0,0,0,0) 100%)',
                  pointerEvents: 'none',
                }}
              />
              <Typography
                component='h3'
                sx={{
                  position: 'absolute',
                  top: { xs: 18, md: 24 },
                  left: { xs: 18, md: 24 },
                  right: 18,
                  color: '#fff',
                  fontWeight: 700,
                  fontSize: { xs: '1.3rem', md: '1.55rem' },
                  lineHeight: 1.15,
                  textShadow: '0 2px 12px rgba(0,0,0,0.35)',
                }}
              >
                {item.title}
              </Typography>
              <Box
                component='button'
                type='button'
                aria-label={isActive ? `Tutup detail ${item.title}` : `Lihat detail ${item.title}`}
                aria-expanded={isActive}
                data-expand={index}
                onClick={(event) => {
                  event.stopPropagation();
                  if (isActive) close();
                  else open();
                }}
                sx={{
                  position: 'absolute',
                  left: { xs: 16, md: 24 },
                  bottom: { xs: 16, md: 24 },
                  width: 44,
                  height: 44,
                  borderRadius: '50%',
                  border: 0,
                  cursor: 'pointer',
                  display: 'grid',
                  placeItems: 'center',
                  color: isActive ? '#fff' : '#111827',
                  backgroundColor: isActive ? '#111827' : '#fff',
                  boxShadow: '0 8px 20px rgba(0,0,0,0.25)',
                  transition: 'transform 0.25s ease, background-color 0.25s ease',
                  '&:hover': { transform: 'scale(1.08)' },
                  '&:focus-visible': { outline: '3px solid #2d6cdf', outlineOffset: 2 },
                }}
              >
                {isActive ? <CloseRoundedIcon sx={{ fontSize: 24 }} /> : <AddRoundedIcon sx={{ fontSize: 26 }} />}
              </Box>
            </Box>

            {/* panel teks */}
            <Box
              aria-hidden={!isActive}
              sx={{
                flex: { xs: '0 0 auto', md: '1 1 0%' },
                minWidth: 0,
                overflow: 'hidden',
                maxHeight: { xs: isActive ? 400 : 0, md: 'none' },
                opacity: isActive ? 1 : 0,
                transition: isActive
                  ? `opacity 0.45s ${EASE} 0.3s, max-height 0.5s ${EASE}`
                  : `opacity 0.15s ${EASE}, max-height 0.4s ${EASE}`,
                display: 'flex',
                alignItems: 'center',
              }}
            >
              <Typography
                sx={{
                  px: { xs: 2.5, md: 3 },
                  py: { xs: 2.5, md: 3 },
                  minWidth: { md: 240 },
                  color: '#374151',
                  fontSize: { xs: '0.95rem', md: '1rem' },
                  lineHeight: 1.7,
                }}
              >
                {item.desc}
              </Typography>
            </Box>
          </Box>
        );
      })}
    </Box>
  );
}

export default ExpandingCards;
